import { useEffect, useState, useRef, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useSociete } from "../contexts/SocieteContext";
import {
  Receipt, Plus, FileText, Download, Send, Trash2, Loader2, Search,
  CheckCircle2, Clock, AlertTriangle, X, ChevronRight, CreditCard,
  TrendingUp, PenLine, Filter, Mail, ArrowDownToLine, Eye, Repeat, Paperclip,
  GraduationCap, Sparkles
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import CatalogSelector from "../components/CatalogSelector";
import PageHeader from "../components/PageHeader";
import YousignSendButton from "../components/integrations/YousignSendButton";
import { TableSkeleton } from "../components/Skeletons";
import SignatureCanvas from "../components/SignatureCanvas";
import RelancesIADialog from "../components/facturation/RelancesIADialog";
import ClientsTab from "../components/facturation/ClientsTab";
import ClientFormDialog from "../components/facturation/ClientFormDialog";
import { KindBadge, LifecycleActions, ExportMenu, ClientInfoEditor } from "../components/facturation/DocLifecycle";

const STATUS_MAP = {
  brouillon: { label: "Brouillon", color: "#64748b", bg: "#64748b20" },
  envoye: { label: "Envoye", color: "#3b82f6", bg: "#3b82f620" },
  signe: { label: "Signe", color: "#8b5cf6", bg: "#8b5cf620" },
  paye: { label: "Paye", color: "#22c55e", bg: "#22c55e20" },
  impaye: { label: "Impaye", color: "#f59e0b", bg: "#f59e0b20" },
  en_retard: { label: "En retard", color: "#ef4444", bg: "#ef444420" },
  annule: { label: "Annule", color: "#6b7280", bg: "#6b728020" },
};
const TYPE_MAP = { devis: "Devis", facture: "Facture", avoir: "Avoir" };
const EMPTY_LINE = { description: "", quantity: 1, unit_price: 0, tva_rate: 20, discount_pct: 0 };
const EMPTY_FORM = () => ({
  type: "facture", client_id: "", lines: [{ ...EMPTY_LINE }],
  due_date: "", payment_terms: "30 jours", notes: "", objet: "", issue_date: new Date().toISOString().split("T")[0],
  catalog_items: [], recurrence: null, attachments: [], lms_enrollments: [], discount_pct: 0, discount_amount: 0,
});
const clientLabel = (c) => c.company_name || c.name || c.email;

export default function FacturationPage() {
  const { user, axiosAuth } = useAuth();
  const { societeApi, currentSociete } = useSociete();
  const api = societeApi();
  const [docs, setDocs] = useState([]);
  const [clients, setClients] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [relanceConfig, setRelanceConfig] = useState(null);
  const [relanceHistory, setRelanceHistory] = useState([]);
  const [showRelancePanel, setShowRelancePanel] = useState(false);
  const [relanceSending, setRelanceSending] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ type: "", status: "", client_id: "", search: "" });
  const [showCreate, setShowCreate] = useState(false);
  const [showRelancesIA, setShowRelancesIA] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [showPayment, setShowPayment] = useState(null);
  const [showSendEmail, setShowSendEmail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM());
  const [tab, setTab] = useState("documents");
  const [showClientForm, setShowClientForm] = useState(false);
  const [payForm, setPayForm] = useState({ amount: 0, method: "virement", date: new Date().toISOString().split("T")[0], notes: "" });
  const [emailForm, setEmailForm] = useState({ email: "", subject: "", body: "" });
  const [payments, setPayments] = useState([]);
  const [gmailStatus, setGmailStatus] = useState(null);
  const [showSign, setShowSign] = useState(null);
  const [signing, setSigning] = useState(false);
  const [docTemplates, setDocTemplates] = useState([]);
  const [lmsSessions, setLmsSessions] = useState([]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [docsR, clientsR, dashR, gmailR, docTplR, relConfR, relHistR, lmsSessR] = await Promise.all([
        api.get("/facturation/documents"),
        api.get("/facturation/clients"),
        api.get("/facturation/dashboard"),
        api.get("/facturation/gmail/status").catch(() => ({ data: { connected: false } })),
        api.get("/catalog/document-templates").catch(() => ({ data: [] })),
        api.get("/facturation/relances/config").catch(() => ({ data: null })),
        api.get("/facturation/relances/history").catch(() => ({ data: [] })),
        api.get("/lms/sessions").catch(() => ({ data: [] })),
      ]);
      setDocs(docsR.data || []);
      setClients(clientsR.data || []);
      setDashboard(dashR.data);
      setGmailStatus(gmailR.data);
      setDocTemplates(docTplR.data || []);
      setRelanceConfig(relConfR.data);
      setRelanceHistory(relHistR.data || []);
      setLmsSessions(lmsSessR.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { reload(); }, [currentSociete]);

  const createDoc = async () => {
    if (!form.client_id) return;
    setSaving(true);
    try {
      const res = await api.post("/facturation/documents", form);
      setShowCreate(false);
      setForm(EMPTY_FORM());
      reload();
      setShowDetail(res.data);
    } catch (e) { alert(e.response?.data?.detail || "Erreur creation"); }
    setSaving(false);
  };

  const onClientCreated = async (c) => {
    try { const r = await api.get("/facturation/clients"); setClients(r.data || []); } catch (e) { console.error(e); }
    if (c?.user_id) setForm(f => ({ ...f, client_id: c.user_id, payment_terms: c.payment_terms || f.payment_terms }));
  };

  const deleteDoc = async (id) => {
    if (!window.confirm("Supprimer ce brouillon ?")) return;
    await api.delete(`/facturation/documents/${id}`);
    setShowDetail(null);
    reload();
  };

  const updateDoc = async (id, data) => {
    await api.put(`/facturation/documents/${id}`, data);
    reload();
    const r = await api.get(`/facturation/documents/${id}`);
    setShowDetail(r.data);
  };

  const generateAvoir = async (id) => {
    try {
      const r = await api.post(`/facturation/documents/${id}/avoir`);
      reload();
      setShowDetail(r.data);
    } catch (e) { alert("Erreur generation avoir"); console.error(e); }
  };

  const recordPayment = async () => {
    if (!showPayment) return;
    setSaving(true);
    try {
      const invId = showPayment;
      await api.put(`/facturation/documents/${invId}/payment`, payForm);
      setShowPayment(null);
      reload();
      const r = await api.get(`/facturation/documents/${invId}`);
      setShowDetail(r.data);
    } catch (e) { alert("Erreur enregistrement paiement"); console.error(e); }
    setSaving(false);
  };

  const loadPayments = async (id) => {
    const r = await api.get(`/facturation/documents/${id}/payments`);
    setPayments(r.data || []);
  };

  const sendEmail = async () => {
    if (!showSendEmail) return;
    setSaving(true);
    try {
      await api.post(`/facturation/documents/${showSendEmail}/send`, emailForm);
      alert("Document envoye !");
      setShowSendEmail(null);
      reload();
      const r = await api.get(`/facturation/documents/${showSendEmail}`);
      setShowDetail(r.data);
    } catch (e) { alert(e.response?.data?.detail || "Erreur envoi"); }
    setSaving(false);
  };

  const downloadPdf = async (id, ref) => {
    const r = await api.get(`/facturation/documents/${id}/pdf`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement("a"); a.href = url; a.download = `${ref}.pdf`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const connectGmail = async () => {
    const r = await api.get("/facturation/gmail/auth-url");
    window.location.href = r.data.auth_url;
  };

  const handleSign = async (signatureData) => {
    if (!showSign) return;
    setSigning(true);
    try {
      const r = await api.post(`/facturation/documents/${showSign}/sign`, {
        signature_data: signatureData,
        signer_name: user?.name || "",
      });
      setShowSign(null);
      reload();
      if (r.data.automation) {
        const auto = r.data.automation;
        const msgs = [];
        if (auto.services_created?.length) msgs.push(`${auto.services_created.length} service(s) active(s)`);
        if (auto.accompaniments_created?.length) msgs.push(`${auto.accompaniments_created.length} accompagnement(s) cree(s)`);
        if (auto.formations_created?.length) msgs.push(`${auto.formations_created.length} inscription(s) formation`);
        if (auto.lms_enrollments_confirmed) msgs.push(`${auto.lms_enrollments_confirmed} inscription(s) LMS confirmee(s)`);
        if (auto.invoices_created?.length) msgs.push(`${auto.invoices_created.length} facture(s) generee(s)`);
        if (msgs.length) alert(`Devis signe ! Automatisation :\n${msgs.join("\n")}`);
        else alert("Document signe !");
      }
    } catch (e) { alert(e.response?.data?.detail || "Erreur signature"); }
    setSigning(false);
  };

  const addAttachment = (tpl) => {
    const current = form.attachments || [];
    setForm(f => ({
      ...f,
      attachments: [...current, {
        attachment_id: `att_${Date.now().toString(36)}`,
        name: tpl.name,
        template_id: tpl.template_id,
        template_name: tpl.name,
        type: "document",
      }]
    }));
  };

  const removeAttachment = (idx) => {
    setForm(f => ({ ...f, attachments: f.attachments.filter((_, i) => i !== idx) }));
  };

  const updateLine = (idx, field, value) => {
    const lines = [...form.lines];
    lines[idx] = { ...lines[idx], [field]: value };
    setForm(f => ({ ...f, lines }));
  };
  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, { ...EMPTY_LINE }] }));
  const removeLine = (idx) => setForm(f => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));

  const calcTotal = (lines, discountPct = 0, discountAmount = 0) => {
    let brut = 0, ht = 0, tva = 0;
    (lines || []).forEach(l => {
      const g = (l.quantity || 0) * (l.unit_price || 0);
      const h = g * (1 - (parseFloat(l.discount_pct) || 0) / 100);
      brut += g; ht += h; tva += h * (l.tva_rate || 0) / 100;
    });
    const pct = parseFloat(discountPct) || 0;
    const global = pct ? ht * pct / 100 : Math.min(parseFloat(discountAmount) || 0, ht);
    if (global > 0 && ht) { tva = tva * (1 - global / ht); ht -= global; }
    return { brut: brut.toFixed(2), remise: (brut - ht).toFixed(2), ht: ht.toFixed(2), tva: tva.toFixed(2), ttc: (ht + tva).toFixed(2) };
  };

  const filtered = docs.filter(d => {
    if (filter.type && d.type !== filter.type) return false;
    if (filter.status && d.status !== filter.status) return false;
    if (filter.client_id && d.client_id !== filter.client_id) return false;
    if (filter.search) {
      const s = filter.search.toLowerCase();
      if (!d.reference?.toLowerCase().includes(s) && !d.client_name?.toLowerCase().includes(s)) return false;
    }
    return true;
  });

  const totals = calcTotal(form.lines, form.discount_pct, form.discount_amount);

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto text-white" data-testid="facturation-page">
      <PageHeader
        icon={<Receipt />}
        title="Facturation"
        description="Devis, factures, avoirs et relances"
        testId="facturation-header"
        actions={(
          <>
            <Button onClick={() => setShowRelancesIA(true)} variant="outline" className="border-[#f7c935]/30 text-[#f7c935] hover:bg-[#f7c935]/10 text-xs" data-testid="relances-ia-btn">
              <Sparkles className="w-4 h-4 mr-1" /> Relances IA
            </Button>
            {gmailStatus && !gmailStatus.connected && (
              <Button onClick={connectGmail} variant="outline" className="border-white/10 text-white hover:bg-white/5 text-xs" data-testid="connect-gmail-btn">
                <Mail className="w-4 h-4 mr-1" /> Connecter Gmail
              </Button>
            )}
            {gmailStatus?.connected && (
              <span className="text-xs text-green-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Gmail connecté</span>
            )}
            <ExportMenu api={api} filter={filter} />
            <Button onClick={() => { setForm({ ...EMPTY_FORM(), client_id: clients[0]?.user_id || "" }); setShowCreate(true); }}
              className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="new-document-btn">
              <Plus className="w-4 h-4 mr-2" /> Nouveau document
            </Button>
          </>
        )}
      />

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-[#14101c] border border-white/5 rounded-xl p-1 w-fit" data-testid="facturation-tabs">
        {[["documents", "Documents"], ["clients", `Clients (${clients.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-colors ${tab === k ? "bg-[#f7c935] text-[#14101c]" : "text-slate-400 hover:text-white"}`} data-testid={`facturation-tab-${k}`}>{l}</button>
        ))}
      </div>

      {tab === "clients" && (
        <ClientsTab api={api} onOpenDoc={(d) => { setShowDetail(d); loadPayments(d.invoice_id); }} onClientsChanged={() => api.get("/facturation/clients").then(r => setClients(r.data || [])).catch(() => {})} />
      )}

      {/* Dashboard cards */}
      {tab === "documents" && dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="facturation-dashboard">
          {[
            { label: "CA Facture", value: `${dashboard.ca_facture?.toLocaleString("fr")} EUR`, color: "#f7c935", icon: TrendingUp },
            { label: "Paye", value: `${dashboard.montant_paye?.toLocaleString("fr")} EUR`, color: "#22c55e", icon: CheckCircle2 },
            { label: "Impaye", value: `${dashboard.montant_impaye?.toLocaleString("fr")} EUR`, color: "#ef4444", icon: AlertTriangle },
            { label: "Devis en attente", value: dashboard.devis_attente, color: "#3b82f6", icon: Clock },
            { label: "En retard", value: dashboard.en_retard, color: "#f59e0b", icon: AlertTriangle },
          ].map((c, i) => (
            <div key={i} className="bg-[#14101c] border border-white/5 rounded-xl p-4">
              <div className="flex items-center gap-2 text-xs text-slate-500 mb-1"><c.icon className="w-3.5 h-3.5" />{c.label}</div>
              <div className="text-xl font-bold" style={{ color: c.color }}>{c.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      {tab === "documents" && <div className="flex gap-2 mb-4 flex-wrap" data-testid="facturation-filters">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input value={filter.search} onChange={e => setFilter(f => ({ ...f, search: e.target.value }))} placeholder="Rechercher..." className="pl-9 bg-[#161020] border-white/10 text-white" data-testid="search-input" />
        </div>
        <select value={filter.type} onChange={e => setFilter(f => ({ ...f, type: e.target.value }))} className="bg-[#161020] border border-white/10 text-white rounded-lg px-3 py-2 text-sm" data-testid="filter-type">
          <option value="">Tous types</option>
          <option value="devis">Devis</option>
          <option value="facture">Facture</option>
          <option value="avoir">Avoir</option>
        </select>
        <select value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))} className="bg-[#161020] border border-white/10 text-white rounded-lg px-3 py-2 text-sm" data-testid="filter-status">
          <option value="">Tous statuts</option>
          {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={filter.client_id} onChange={e => setFilter(f => ({ ...f, client_id: e.target.value }))} className="bg-[#161020] border border-white/10 text-white rounded-lg px-3 py-2 text-sm" data-testid="filter-client">
          <option value="">Tous clients</option>
          {clients.map(c => <option key={c.user_id} value={c.user_id}>{clientLabel(c)}</option>)}
        </select>
      </div>}

      {/* Documents list */}
      {tab !== "documents" ? null : loading ? (
        <TableSkeleton rows={5} cols={4} testId="facturation-loading-skeleton" />
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-500"><Receipt className="w-12 h-12 mx-auto mb-3 text-slate-600" /><p>Aucun document</p></div>
      ) : (
        <div className="space-y-2" data-testid="documents-list">
          {filtered.map(d => {
            const st = STATUS_MAP[d.status] || STATUS_MAP.brouillon;
            return (
              <div key={d.invoice_id} onClick={() => { setShowDetail(d); loadPayments(d.invoice_id); }} className="bg-[#14101c] border border-white/5 rounded-xl p-4 hover:border-[#f7c935]/20 cursor-pointer transition-colors" data-testid={`doc-${d.invoice_id}`}>
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-3 min-w-[200px]">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center text-xs font-bold" style={{ background: d.type === "devis" ? "#3b82f620" : d.type === "avoir" ? "#f59e0b20" : "#f7c93520", color: d.type === "devis" ? "#3b82f6" : d.type === "avoir" ? "#f59e0b" : "#f7c935" }}>
                      {TYPE_MAP[d.type]?.slice(0, 3)}
                    </div>
                    <div>
                      <span className="text-sm font-bold text-slate-200">{d.reference}</span>
                      <span className="text-xs text-slate-500 block">{d.client_name}</span>
                    </div>
                  </div>
                  <span className="text-xs px-2 py-1 rounded-full font-medium" style={{ background: st.bg, color: st.color }}>{st.label}</span>
                  <KindBadge doc={d} />
                  <span className="text-sm font-bold text-slate-200 ml-auto">{d.total_ttc?.toLocaleString("fr", { minimumFractionDigits: 2 })} EUR</span>
                  {d.relance_count > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-500/10 text-orange-400">{d.relance_count} relance{d.relance_count > 1 ? "s" : ""}</span>}
                  <span className="text-xs text-slate-500">{d.issue_date}</span>
                  <ChevronRight className="w-4 h-4 text-slate-600" />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ─── Relances automatiques ─── */}
      {tab === "documents" && <div className="mt-6 mb-4">
        <button onClick={() => setShowRelancePanel(!showRelancePanel)} className="flex items-center gap-2 text-sm font-bold text-[#f7c935] hover:underline" data-testid="toggle-relance-panel">
          <Repeat className="w-4 h-4" /> Relances automatiques
          <span className="text-xs text-slate-500 ml-1">({relanceHistory.length} envoyees)</span>
        </button>

        {showRelancePanel && (
          <div className="mt-3 space-y-4 animate-fade-in-up">
            {/* Config */}
            {relanceConfig && (
              <div className="bg-[#14101c] border border-white/5 rounded-xl p-5" data-testid="relance-config">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold">Configuration</h3>
                  <button onClick={async () => {
                    const newEnabled = !relanceConfig.enabled;
                    await api.put("/facturation/relances/config", { enabled: newEnabled });
                    setRelanceConfig(c => ({ ...c, enabled: newEnabled }));
                  }} className={`text-xs px-3 py-1 rounded-full font-medium transition-colors ${relanceConfig.enabled ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`} data-testid="relance-toggle">
                    {relanceConfig.enabled ? "Active" : "Desactive"}
                  </button>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] uppercase text-slate-500 mb-1 block">Intervalles de relance (jours apres echeance)</label>
                    <div className="flex gap-2">
                      {(relanceConfig.intervals || []).map((iv, idx) => (
                        <div key={idx} className="flex items-center gap-1">
                          <input type="number" value={iv} min={1} onChange={e => {
                            const newIntervals = [...relanceConfig.intervals];
                            newIntervals[idx] = parseInt(e.target.value) || 1;
                            setRelanceConfig(c => ({ ...c, intervals: newIntervals }));
                          }} className="w-14 bg-[#161020] border border-white/10 text-white rounded px-2 py-1 text-sm text-center" />
                          <span className="text-xs text-slate-500">j</span>
                        </div>
                      ))}
                      <button onClick={() => setRelanceConfig(c => ({ ...c, intervals: [...(c.intervals || []), 45] }))} className="text-xs text-[#f7c935] hover:underline">+ Ajouter</button>
                    </div>
                    <button onClick={async () => {
                      await api.put("/facturation/relances/config", { intervals: relanceConfig.intervals });
                    }} className="mt-2 text-xs text-green-400 hover:underline" data-testid="save-relance-config">Sauvegarder</button>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase text-slate-500 mb-1 block">Fonctionnement</label>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      Le cron quotidien verifie les factures impayees/en retard. Une relance par email + notification est envoyee au client a chaque intervalle configure.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Overdue invoices with manual send */}
            {(() => {
              const overdue = docs.filter(d => d.type === "facture" && ["en_retard", "impaye"].includes(d.status));
              return overdue.length > 0 ? (
                <div className="bg-[#14101c] border border-white/5 rounded-xl p-5" data-testid="relance-overdue-list">
                  <h3 className="text-sm font-bold mb-3">Factures en attente de paiement ({overdue.length})</h3>
                  <div className="space-y-2">
                    {overdue.map(d => (
                      <div key={d.invoice_id} className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/5" data-testid={`relance-invoice-${d.invoice_id}`}>
                        <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium">{d.reference}</span>
                          <span className="text-xs text-slate-500 ml-2">{d.client_name}</span>
                        </div>
                        <span className="text-sm font-bold text-red-400">{d.total_ttc?.toLocaleString("fr", { minimumFractionDigits: 2 })} EUR</span>
                        {d.relance_count > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-yellow-500/10 text-yellow-400">{d.relance_count} relance{d.relance_count > 1 ? "s" : ""}</span>}
                        <Button size="sm" onClick={async (e) => {
                          e.stopPropagation();
                          setRelanceSending(d.invoice_id);
                          try {
                            await api.post("/facturation/relances/send-now", { invoice_id: d.invoice_id });
                            reload();
                          } catch (err) { alert("Erreur envoi relance"); }
                          setRelanceSending(null);
                        }} disabled={relanceSending === d.invoice_id} className="bg-red-500/10 text-red-400 hover:bg-red-500/20 border-0 text-xs h-7 px-2" data-testid={`send-relance-${d.invoice_id}`}>
                          {relanceSending === d.invoice_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Send className="w-3 h-3 mr-1" /> Relancer</>}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null;
            })()}

            {/* Recent history */}
            {relanceHistory.length > 0 && (
              <div className="bg-[#14101c] border border-white/5 rounded-xl p-5" data-testid="relance-history">
                <h3 className="text-sm font-bold mb-3">Historique des relances</h3>
                <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                  {relanceHistory.slice(0, 20).map(r => (
                    <div key={r.relance_id} className="flex items-center gap-3 text-xs p-2 rounded-lg bg-white/[0.02]">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${r.email_sent ? "bg-green-400" : "bg-yellow-400"}`} />
                      <span className="text-slate-400 w-20 flex-shrink-0">{r.sent_at ? new Date(r.sent_at).toLocaleDateString("fr-FR") : ""}</span>
                      <span className="font-medium text-slate-200">{r.reference}</span>
                      <span className="text-slate-500 truncate flex-1">{r.client_name}</span>
                      <span className="text-red-400 font-bold">{r.remaining?.toFixed(2)} EUR</span>
                      <span className={`px-1.5 py-0.5 rounded-full ${r.manual ? "bg-blue-500/10 text-blue-400" : "bg-purple-500/10 text-purple-400"}`}>
                        {r.manual ? "Manuel" : "Auto"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>}

      <ClientFormDialog open={showClientForm} onClose={() => setShowClientForm(false)} api={api} client={null} onSaved={onClientCreated} />

      {/* ─── Relances IA Dialog ─── */}
      <RelancesIADialog open={showRelancesIA} onClose={() => setShowRelancesIA(false)} api={api} onSent={reload} />

      {/* ─── Create Dialog ─── */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nouveau document</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Type</label>
                <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className="w-full bg-[#161020] border border-white/10 text-white rounded-lg px-3 py-2 text-sm" data-testid="form-type">
                  <option value="facture">Facture</option>
                  <option value="devis">Devis</option>
                </select>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-slate-500 block">Client</label>
                  <button onClick={() => setShowClientForm(true)} className="text-[11px] text-[#f7c935] hover:underline flex items-center gap-0.5" data-testid="new-client-btn"><Plus className="w-3 h-3" /> Nouveau client</button>
                </div>
                <select value={form.client_id} onChange={e => { const c = clients.find(x => x.user_id === e.target.value); setForm(f => ({ ...f, client_id: e.target.value, payment_terms: c?.payment_terms || f.payment_terms })); }} className="w-full bg-[#161020] border border-white/10 text-white rounded-lg px-3 py-2 text-sm" data-testid="form-client">
                  <option value="">-- Choisir --</option>
                  {clients.map(c => <option key={c.user_id} value={c.user_id}>{clientLabel(c)}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Date emission</label>
                <Input type="date" value={form.issue_date} onChange={e => setForm(f => ({ ...f, issue_date: e.target.value }))} className="bg-[#161020] border-white/10 text-white" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Echeance</label>
                <Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} className="bg-[#161020] border-white/10 text-white" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Objet</label>
                <Input value={form.objet} onChange={e => setForm(f => ({ ...f, objet: e.target.value }))} placeholder="Objet du document" className="bg-[#161020] border-white/10 text-white" data-testid="form-objet" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Conditions de paiement</label>
                <Input value={form.payment_terms} onChange={e => setForm(f => ({ ...f, payment_terms: e.target.value }))} className="bg-[#161020] border-white/10 text-white" data-testid="form-payment-terms" />
              </div>
            </div>

            {/* Lines */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-slate-500 font-bold">Lignes</label>
                <button onClick={addLine} className="text-xs text-[#f7c935] hover:underline flex items-center gap-1" data-testid="add-line-btn"><Plus className="w-3 h-3" /> Ajouter</button>
              </div>
              <div className="space-y-2">
                {form.lines.map((l, i) => (
                  <div key={i} className="grid grid-cols-[1fr_55px_75px_55px_55px_30px] gap-2 items-center" data-testid={`line-${i}`}>
                    <Input value={l.description} onChange={e => updateLine(i, "description", e.target.value)} placeholder="Description" className="bg-[#161020] border-white/10 text-white text-sm" />
                    <Input type="number" value={l.quantity} onChange={e => updateLine(i, "quantity", parseFloat(e.target.value) || 0)} className="bg-[#161020] border-white/10 text-white text-sm text-center" />
                    <Input type="number" value={l.unit_price} onChange={e => updateLine(i, "unit_price", parseFloat(e.target.value) || 0)} className="bg-[#161020] border-white/10 text-white text-sm text-center" />
                    <Input type="number" min={0} max={100} value={l.discount_pct ?? 0} onChange={e => updateLine(i, "discount_pct", parseFloat(e.target.value) || 0)} className="bg-[#161020] border-white/10 text-white text-sm text-center" data-testid={`line-${i}-discount`} />
                    <Input type="number" value={l.tva_rate} onChange={e => updateLine(i, "tva_rate", parseFloat(e.target.value) || 0)} className="bg-[#161020] border-white/10 text-white text-sm text-center" />
                    {form.lines.length > 1 && <button onClick={() => removeLine(i)} className="text-slate-500 hover:text-red-400"><X className="w-4 h-4" /></button>}
                  </div>
                ))}
                <div className="text-[10px] text-slate-600 grid grid-cols-[1fr_55px_75px_55px_55px_30px] gap-2 px-1">
                  <span>Description</span><span className="text-center">Qte</span><span className="text-center">P.U. HT</span><span className="text-center">Remise%</span><span className="text-center">TVA%</span><span></span>
                </div>
              </div>
            </div>

            {/* Remise globale */}
            <div className="flex items-center gap-2 text-xs" data-testid="global-discount">
              <span className="text-slate-500">Remise globale</span>
              <Input type="number" min={0} max={100} value={form.discount_pct} onChange={e => setForm(f => ({ ...f, discount_pct: parseFloat(e.target.value) || 0, discount_amount: 0 }))} className="bg-[#161020] border-white/10 text-white h-8 w-20 text-center" data-testid="global-discount-pct" />
              <span className="text-slate-500">%</span>
              <span className="text-slate-600">ou</span>
              <Input type="number" min={0} value={form.discount_amount} onChange={e => setForm(f => ({ ...f, discount_amount: parseFloat(e.target.value) || 0, discount_pct: 0 }))} className="bg-[#161020] border-white/10 text-white h-8 w-24 text-center" data-testid="global-discount-amount" />
              <span className="text-slate-500">EUR HT</span>
            </div>

            {/* Totals preview */}
            <div className="bg-[#161020] rounded-lg p-3 text-sm space-y-1" data-testid="totals-preview">
              {parseFloat(totals.remise) > 0 && <><div className="flex justify-between"><span className="text-slate-500">Total HT brut</span><span>{totals.brut} EUR</span></div><div className="flex justify-between text-emerald-400"><span>Remise</span><span>-{totals.remise} EUR</span></div></>}
              <div className="flex justify-between"><span className="text-slate-500">Total HT</span><span>{totals.ht} EUR</span></div>
              <div className="flex justify-between"><span className="text-slate-500">TVA</span><span>{totals.tva} EUR</span></div>
              <div className="flex justify-between font-bold text-[#f7c935]"><span>Total TTC</span><span>{totals.ttc} EUR</span></div>
            </div>

            <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes (optionnel)" rows={2} className="bg-[#161020] border-white/10 text-white text-sm" />

            {/* Catalog selection */}
            <CatalogSelector api={api} selected={form.catalog_items || []} onChange={items => {
              // Auto-add catalog items as lines
              const existingDescs = form.lines.map(l => l.description);
              const newLines = items.filter(it => !existingDescs.includes(it.name)).map(it => ({
                description: it.name,
                quantity: 1,
                unit_price: parseFloat(it.price) || 0,
                tva_rate: 20,
              }));
              setForm(f => ({ ...f, catalog_items: items, lines: [...f.lines, ...newLines] }));
            }} />

            {/* Attachments (devis only) */}
            {form.type === "devis" && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs text-slate-500 font-bold flex items-center gap-1"><Paperclip className="w-3 h-3" /> Pieces jointes (contrats, documents)</label>
                </div>
                {(form.attachments || []).map((att, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-[#161020] border border-white/5 mb-1.5">
                    <FileText className="w-3.5 h-3.5 text-blue-400" />
                    <span className="text-xs flex-1">{att.name}</span>
                    <button onClick={() => removeAttachment(i)} className="p-0.5 hover:bg-red-500/10 rounded"><X className="w-3 h-3 text-red-400" /></button>
                  </div>
                ))}
                {docTemplates.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {docTemplates.filter(t => !(form.attachments || []).some(a => a.template_id === t.template_id)).slice(0, 6).map(tpl => (
                      <button key={tpl.template_id} onClick={() => addAttachment(tpl)}
                        className="text-[10px] px-2 py-1 rounded-lg bg-white/5 border border-white/10 text-slate-400 hover:text-white hover:border-white/20 transition-colors flex items-center gap-1"
                        data-testid={`attach-${tpl.template_id}`}>
                        <Plus className="w-2.5 h-2.5" /> {tpl.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Recurring invoicing (devis only) */}
            {form.type === "devis" && (
              <div className="bg-[#161020] rounded-lg p-3 border border-white/5">
                <label className="flex items-center gap-2 text-xs cursor-pointer mb-2">
                  <input
                    type="checkbox"
                    checked={!!form.recurrence?.enabled}
                    onChange={e => setForm(f => ({
                      ...f,
                      recurrence: e.target.checked
                        ? { enabled: true, frequency: 1, unit: "month", count: 3, start_date: f.issue_date }
                        : null
                    }))}
                    className="w-4 h-4 rounded"
                    data-testid="recurrence-toggle"
                  />
                  <Repeat className="w-3.5 h-3.5 text-[#f7c935]" />
                  <span className="font-bold text-slate-300">Facturation recurrente</span>
                </label>
                {form.recurrence?.enabled && (
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    <div>
                      <label className="text-[10px] text-slate-600 block mb-0.5">Tous les</label>
                      <Input type="number" min={1} value={form.recurrence.frequency} onChange={e => setForm(f => ({ ...f, recurrence: { ...f.recurrence, frequency: parseInt(e.target.value) || 1 } }))} className="bg-[#14101c] border-white/10 text-white text-xs h-8" data-testid="recurrence-frequency" />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-600 block mb-0.5">Unite</label>
                      <select value={form.recurrence.unit} onChange={e => setForm(f => ({ ...f, recurrence: { ...f.recurrence, unit: e.target.value } }))} className="w-full bg-[#14101c] border border-white/10 text-white rounded-lg px-2 py-1 text-xs h-8" data-testid="recurrence-unit">
                        <option value="day">Jour(s)</option>
                        <option value="week">Semaine(s)</option>
                        <option value="month">Mois</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-600 block mb-0.5">Nb factures</label>
                      <Input type="number" min={1} max={36} value={form.recurrence.count} onChange={e => setForm(f => ({ ...f, recurrence: { ...f.recurrence, count: parseInt(e.target.value) || 1 } }))} className="bg-[#14101c] border-white/10 text-white text-xs h-8" data-testid="recurrence-count" />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-600 block mb-0.5">Debut</label>
                      <Input type="date" value={form.recurrence.start_date} onChange={e => setForm(f => ({ ...f, recurrence: { ...f.recurrence, start_date: e.target.value } }))} className="bg-[#14101c] border-white/10 text-white text-xs h-8" />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* LMS enrollments link (devis only) */}
            {form.type === "devis" && (
              <div className="bg-[#161020] rounded-lg p-3 border border-white/5" data-testid="lms-enrollments-block">
                <div className="flex items-center gap-2 mb-2">
                  <GraduationCap className="w-3.5 h-3.5 text-[#f7c935]" />
                  <span className="text-xs font-bold text-slate-300">Inscription a une session de formation</span>
                </div>
                <p className="text-[10px] text-slate-500 mb-2">Stagiaires crees en statut <span className="text-amber-400 font-semibold">« Devis envoye »</span>. A la signature → <span className="text-purple-400 font-semibold">« Devis signe »</span>. Au paiement (acompte ou complet) → <span className="text-green-400 font-semibold">« Inscrit »</span>.</p>
                {(form.lms_enrollments || []).map((item, i) => {
                  const sessObj = lmsSessions.find((s) => s.session_id === item.session_id);
                  // Filter sessions by selected program (if any)
                  const filteredSessions = item.program_id
                    ? lmsSessions.filter((s) => s.program_id === item.program_id)
                    : lmsSessions;
                  // Unique program list from sessions
                  const progOptions = Array.from(new Map(lmsSessions.map((s) => [s.program_id, { program_id: s.program_id, program_title: s.program_title }])).values());
                  return (
                    <div key={i} className="space-y-2 p-2 bg-[#0b0811] border border-white/5 rounded mb-2" data-testid={`lms-enroll-${i}`}>
                      <div className="flex items-center gap-2">
                        <select value={item.program_id || ""} onChange={(e) => setForm(f => { const a = [...f.lms_enrollments]; a[i] = { ...a[i], program_id: e.target.value, session_id: "" }; return { ...f, lms_enrollments: a }; })} className="flex-1 bg-[#14101c] border border-white/10 text-white rounded px-2 py-1.5 text-xs" data-testid={`lms-enroll-program-${i}`}>
                          <option value="">— Programme (produit formation) —</option>
                          {progOptions.map((p) => (
                            <option key={p.program_id} value={p.program_id}>{p.program_title}</option>
                          ))}
                        </select>
                        <button onClick={() => setForm(f => ({ ...f, lms_enrollments: f.lms_enrollments.filter((_, idx) => idx !== i) }))} className="p-1 text-red-400 hover:bg-red-500/10 rounded" data-testid={`lms-enroll-del-${i}`}><X className="w-3.5 h-3.5" /></button>
                      </div>
                      <select value={item.session_id} onChange={(e) => setForm(f => { const a = [...f.lms_enrollments]; a[i] = { ...a[i], session_id: e.target.value }; return { ...f, lms_enrollments: a }; })} className="w-full bg-[#14101c] border border-white/10 text-white rounded px-2 py-1.5 text-xs" data-testid={`lms-enroll-session-${i}`} disabled={!item.program_id && filteredSessions.length > 10}>
                        <option value="">— Session —</option>
                        {filteredSessions.map((s) => (
                          <option key={s.session_id} value={s.session_id}>{s.title} ({s.start_date} → {s.end_date})</option>
                        ))}
                      </select>
                      {sessObj && (
                        <div className="text-[10px] text-slate-500">{sessObj.location || "presentiel"} · {sessObj.program_duration_hours || 0}h · Max {sessObj.max_participants} pers.</div>
                      )}
                      <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="Nom stagiaire *" value={item.student_name} onChange={(e) => setForm(f => { const a = [...f.lms_enrollments]; a[i] = { ...a[i], student_name: e.target.value }; return { ...f, lms_enrollments: a }; })} className="bg-[#14101c] border-white/10 text-white text-xs h-8" data-testid={`lms-enroll-name-${i}`} />
                        <Input placeholder="Email stagiaire *" value={item.student_email} onChange={(e) => setForm(f => { const a = [...f.lms_enrollments]; a[i] = { ...a[i], student_email: e.target.value }; return { ...f, lms_enrollments: a }; })} className="bg-[#14101c] border-white/10 text-white text-xs h-8" data-testid={`lms-enroll-email-${i}`} />
                        <Input placeholder="Telephone" value={item.student_phone || ""} onChange={(e) => setForm(f => { const a = [...f.lms_enrollments]; a[i] = { ...a[i], student_phone: e.target.value }; return { ...f, lms_enrollments: a }; })} className="bg-[#14101c] border-white/10 text-white text-xs h-8" />
                        <Input placeholder="Entreprise" value={item.student_company || ""} onChange={(e) => setForm(f => { const a = [...f.lms_enrollments]; a[i] = { ...a[i], student_company: e.target.value }; return { ...f, lms_enrollments: a }; })} className="bg-[#14101c] border-white/10 text-white text-xs h-8" />
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <select value={item.payment_method || ""} onChange={(e) => setForm(f => { const a = [...f.lms_enrollments]; a[i] = { ...a[i], payment_method: e.target.value }; return { ...f, lms_enrollments: a }; })} className="bg-[#14101c] border border-white/10 text-white rounded px-2 py-1.5 text-xs" data-testid={`lms-enroll-payment-${i}`}>
                          <option value="">Modalite paiement…</option>
                          <option value="comptant">Comptant</option>
                          <option value="subrogation">Subrogation</option>
                          <option value="credit">Credit</option>
                          <option value="cpf">CPF</option>
                          <option value="opco">OPCO</option>
                          <option value="autre">Autre</option>
                        </select>
                        <Input placeholder="Financeur (si subrogation)" value={item.financeur_name || ""} onChange={(e) => setForm(f => { const a = [...f.lms_enrollments]; a[i] = { ...a[i], financeur_name: e.target.value }; return { ...f, lms_enrollments: a }; })} className="bg-[#14101c] border-white/10 text-white text-xs h-8" data-testid={`lms-enroll-financeur-${i}`} />
                        <Input placeholder="SIRET financeur" value={item.financeur_siret || ""} onChange={(e) => setForm(f => { const a = [...f.lms_enrollments]; a[i] = { ...a[i], financeur_siret: e.target.value }; return { ...f, lms_enrollments: a }; })} className="bg-[#14101c] border-white/10 text-white text-xs h-8" />
                      </div>
                    </div>
                  );
                })}
                <button onClick={() => setForm(f => ({ ...f, lms_enrollments: [...(f.lms_enrollments || []), { program_id: "", session_id: "", student_name: "", student_email: "", student_phone: "", student_company: "", payment_method: "", financeur_name: "", financeur_siret: "" }] }))} className="text-xs text-[#f7c935] hover:underline flex items-center gap-1" data-testid="lms-enroll-add">
                  <Plus className="w-3 h-3" /> Ajouter un stagiaire sur une session
                </button>
              </div>
            )}

            <Button onClick={createDoc} disabled={saving || !form.client_id} className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="confirm-create-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} Creer {TYPE_MAP[form.type]}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Detail Dialog ─── */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          {showDetail && (() => {
            const d = showDetail;
            const st = STATUS_MAP[d.status] || STATUS_MAP.brouillon;
            const remaining = Math.max(0, Math.round((Math.abs(d.total_ttc || 0) - (d.paid_amount || 0)) * 100) / 100);
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3">
                    <span>{TYPE_MAP[d.type]} {d.reference}</span>
                    <span className="text-xs px-2 py-1 rounded-full font-medium" style={{ background: st.bg, color: st.color }}>{st.label}</span>
                    <KindBadge doc={d} />
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <ClientInfoEditor api={api} doc={d} onSaved={async () => { const r = await api.get(`/facturation/documents/${d.invoice_id}`); setShowDetail(r.data); reload(); }} />
                  {d.parent_ref && <div className="text-xs text-slate-400 bg-[#161020] rounded-lg p-2">Se rapporte au devis <b>{d.parent_ref}</b>{d.acompte_refs?.length ? ` — acomptes déduits : ${d.acompte_refs.join(", ")}` : ""}</div>}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {d.objet && <div className="col-span-2"><span className="text-slate-500">Objet:</span> <span>{d.objet}</span></div>}
                    <div><span className="text-slate-500">Date:</span> <span>{d.issue_date}</span></div>
                    <div><span className="text-slate-500">Echeance:</span> <span>{d.due_date || "-"}</span></div>
                    {d.sent_at && <div className="col-span-2"><span className="text-slate-500">Envoye le:</span> <span className="text-blue-400">{d.sent_at.slice(0, 10)}</span></div>}
                    {d.signed_at && <div className="col-span-2"><span className="text-slate-500">Signe le:</span> <span className="text-purple-400">{d.signed_at.slice(0, 10)}</span></div>}
                  </div>

                  {/* Lines table */}
                  <div className="bg-[#161020] rounded-lg overflow-hidden">
                    <div className="grid grid-cols-[1fr_50px_70px_50px_80px] gap-2 p-2 text-[10px] text-slate-500 font-bold border-b border-white/5">
                      <span>Description</span><span>Qte</span><span>P.U.</span><span>TVA</span><span className="text-right">TTC</span>
                    </div>
                    {(d.lines || []).map((l, i) => (
                      <div key={i} className="grid grid-cols-[1fr_50px_70px_50px_80px] gap-2 p-2 text-sm border-b border-white/5 last:border-0">
                        <span className="text-slate-300">{l.description}</span>
                        <span className="text-slate-400">{l.quantity}</span>
                        <span className="text-slate-400">{parseFloat(l.unit_price || 0).toFixed(2)}</span>
                        <span className="text-slate-400">{l.tva_rate || 0}%</span>
                        <span className="text-right font-medium">{parseFloat(l.total_ttc || 0).toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="p-3 space-y-1 border-t border-white/10 text-sm">
                      {(d.discount_global > 0 || d.discount_lines > 0) && <><div className="flex justify-between"><span className="text-slate-500">Total HT brut</span><span>{d.total_ht_brut?.toFixed(2)} EUR</span></div><div className="flex justify-between text-emerald-400"><span>Remise</span><span>-{((d.discount_global || 0) + (d.discount_lines || 0)).toFixed(2)} EUR</span></div></>}
                      <div className="flex justify-between"><span className="text-slate-500">Total HT</span><span>{d.total_ht?.toFixed(2)} EUR</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">TVA</span><span>{d.total_tva?.toFixed(2)} EUR</span></div>
                      <div className="flex justify-between font-bold text-[#f7c935] text-base"><span>Total TTC</span><span>{d.total_ttc?.toFixed(2)} EUR</span></div>
                    </div>
                  </div>

                  {/* Payments */}
                  {d.type === "facture" && (
                    <div className="bg-[#161020] rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-400">Encaissements</span>
                        <span className="text-xs text-slate-500">Paye: {(d.paid_amount || 0).toFixed(2)} / {Math.abs(d.total_ttc || 0).toFixed(2)} EUR</span>
                      </div>
                      <div className="h-2 bg-[#14101c] rounded-full overflow-hidden">
                        <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${Math.min(100, ((d.paid_amount || 0) / Math.abs(d.total_ttc || 1)) * 100)}%` }} />
                      </div>
                      {remaining > 0 && <div className="text-xs text-red-400">Reste a payer: {remaining.toFixed(2)} EUR</div>}
                      {payments.length > 0 && (
                        <div className="space-y-1 mt-2">
                          {payments.map(p => (
                            <div key={p.payment_id} className="flex items-center gap-2 text-xs text-slate-400 bg-[#14101c] rounded p-2">
                              <CreditCard className="w-3 h-3 text-green-400" />
                              <span>{p.amount?.toFixed(2)} EUR</span>
                              <span className="text-slate-600">({p.method})</span>
                              <span className="text-slate-600 ml-auto">{p.date}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* LMS enrollments linked */}
                  {d.lms_enrollment_ids?.length > 0 && (
                    <div className="bg-[#161020] rounded-lg p-3" data-testid="detail-lms-block">
                      <span className="text-xs font-bold text-slate-400 flex items-center gap-1 mb-2"><GraduationCap className="w-3 h-3 text-[#f7c935]" /> Inscriptions session ({d.lms_enrollment_ids.length})</span>
                      <p className="text-[10px] text-slate-500">{d.status === "signe" || d.status === "paye" ? "Stagiaires inscrits sur la session." : "Stagiaires en attente — ils deviendront inscrits a la signature du devis."}</p>
                    </div>
                  )}

                  {/* Catalog items linked */}
                  {d.catalog_items?.length > 0 && (
                    <div className="bg-[#161020] rounded-lg p-3">
                      <span className="text-xs font-bold text-slate-400 block mb-2">Elements du catalogue</span>
                      <div className="space-y-1">
                        {d.catalog_items.map((item, i) => (
                          <div key={i} className="text-xs flex items-center gap-2 text-slate-300">
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/5">{item.type}</span>
                            <span>{item.name}</span>
                            {item.price && <span className="text-slate-500 ml-auto">{item.price}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Attachments */}
                  {d.attachments?.length > 0 && (
                    <div className="bg-[#161020] rounded-lg p-3">
                      <span className="text-xs font-bold text-slate-400 block mb-2">Pieces jointes</span>
                      <div className="space-y-1">
                        {d.attachments.map((att, i) => (
                          <div key={i} className="text-xs flex items-center gap-2 text-slate-300">
                            <Paperclip className="w-3 h-3 text-blue-400" />
                            <span>{att.name || att.template_name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Signature display */}
                  {d.signature_data && (
                    <div className="bg-[#161020] rounded-lg p-3">
                      <span className="text-xs font-bold text-slate-400 block mb-2">Signature - {d.signed_by}</span>
                      <img src={d.signature_data} alt="Signature" className="h-16 rounded border border-white/10" data-testid="signature-image" />
                    </div>
                  )}

                  {/* Recurrence info */}
                  {d.recurrence_total > 1 && (
                    <div className="text-xs text-slate-400 flex items-center gap-1.5 bg-[#161020] rounded-lg p-2.5">
                      <Repeat className="w-3.5 h-3.5 text-[#f7c935]" />
                      <span>Facture {(d.recurrence_index || 0) + 1}/{d.recurrence_total} (recurrente depuis {d.devis_ref})</span>
                    </div>
                  )}

                  {d.notes && <div className="text-sm text-slate-400 bg-[#161020] rounded-lg p-3"><span className="text-xs text-slate-500 block mb-1">Notes</span>{d.notes}</div>}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    <LifecycleActions api={api} doc={d} onCreated={(nd) => { reload(); setShowDetail(nd); loadPayments(nd.invoice_id); }} />
                    <Button onClick={() => downloadPdf(d.invoice_id, d.reference)} variant="outline" className="border-white/10 text-white hover:bg-white/5 text-xs" data-testid="download-pdf-btn">
                      <Download className="w-4 h-4 mr-1" /> PDF
                    </Button>
                    <Button onClick={() => { setEmailForm({ email: d.client_email || "", subject: `${TYPE_MAP[d.type]} ${d.reference}`, body: `Bonjour ${d.client_name},\n\nVeuillez trouver ci-joint votre ${(TYPE_MAP[d.type] || "").toLowerCase()} ${d.reference}.\n\nCordialement` }); setShowSendEmail(d.invoice_id); }} variant="outline" className="border-white/10 text-white hover:bg-white/5 text-xs" data-testid="send-email-btn">
                      <Send className="w-4 h-4 mr-1" /> Envoyer
                    </Button>
                    {(d.status === "brouillon" || d.status === "envoye") && !d.signature_data && (
                      <Button onClick={() => setShowSign(d.invoice_id)} className="bg-purple-600 text-white hover:bg-purple-700 text-xs" data-testid="sign-doc-btn">
                        <PenLine className="w-4 h-4 mr-1" /> Signer
                      </Button>
                    )}
                    {d.type === "devis" && (d.status === "brouillon" || d.status === "envoye") && !d.signature_data && (
                      <YousignSendButton contextType="devis" contextId={d.invoice_id} onSent={() => reload()} />
                    )}
                    {d.type === "facture" && d.status !== "paye" && (
                      <Button onClick={() => { setPayForm({ amount: remaining, method: "virement", date: new Date().toISOString().split("T")[0], notes: "" }); setShowPayment(d.invoice_id); }} className="bg-green-600 text-white hover:bg-green-700 text-xs" data-testid="record-payment-btn">
                        <CreditCard className="w-4 h-4 mr-1" /> Encaisser
                      </Button>
                    )}
                    {d.type === "facture" && d.status !== "brouillon" && (
                      <Button onClick={() => generateAvoir(d.invoice_id)} variant="outline" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 text-xs" data-testid="generate-avoir-btn">
                        <ArrowDownToLine className="w-4 h-4 mr-1" /> Avoir
                      </Button>
                    )}
                    {d.status === "brouillon" && (
                      <Button onClick={() => deleteDoc(d.invoice_id)} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs" data-testid="delete-doc-btn">
                        <Trash2 className="w-4 h-4 mr-1" /> Supprimer
                      </Button>
                    )}
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ─── Payment Dialog ─── */}
      <Dialog open={!!showPayment} onOpenChange={() => setShowPayment(null)}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle>Enregistrer un paiement</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Montant</label>
              <Input type="number" value={payForm.amount} onChange={e => setPayForm(f => ({ ...f, amount: parseFloat(e.target.value) || 0 }))} className="bg-[#161020] border-white/10 text-white" data-testid="payment-amount" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Methode</label>
              <select value={payForm.method} onChange={e => setPayForm(f => ({ ...f, method: e.target.value }))} className="w-full bg-[#161020] border border-white/10 text-white rounded-lg px-3 py-2 text-sm" data-testid="payment-method">
                <option value="virement">Virement</option>
                <option value="cheque">Cheque</option>
                <option value="carte">Carte bancaire</option>
                <option value="especes">Especes</option>
                <option value="prelevement">Prelevement</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Date</label>
              <Input type="date" value={payForm.date} onChange={e => setPayForm(f => ({ ...f, date: e.target.value }))} className="bg-[#161020] border-white/10 text-white" />
            </div>
            <Button onClick={recordPayment} disabled={saving} className="w-full bg-green-600 text-white hover:bg-green-700 font-bold" data-testid="confirm-payment-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />} Enregistrer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Send Email Dialog ─── */}
      <Dialog open={!!showSendEmail} onOpenChange={() => setShowSendEmail(null)}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle>Envoyer le document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Destinataire</label>
              <Input value={emailForm.email} onChange={e => setEmailForm(f => ({ ...f, email: e.target.value }))} className="bg-[#161020] border-white/10 text-white" data-testid="email-recipient" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Objet</label>
              <Input value={emailForm.subject} onChange={e => setEmailForm(f => ({ ...f, subject: e.target.value }))} className="bg-[#161020] border-white/10 text-white" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Message</label>
              <Textarea value={emailForm.body} onChange={e => setEmailForm(f => ({ ...f, body: e.target.value }))} rows={4} className="bg-[#161020] border-white/10 text-white text-sm" />
            </div>
            <p className="text-[10px] text-slate-600">Le PDF sera joint automatiquement. {!gmailStatus?.connected && "Gmail non connecte - Resend sera utilise."}</p>
            <Button onClick={sendEmail} disabled={saving || !emailForm.email} className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="confirm-send-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />} Envoyer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Signature Dialog ─── */}
      <Dialog open={!!showSign} onOpenChange={() => setShowSign(null)}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><PenLine className="w-5 h-5 text-purple-400" /> Signer le document</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-400 mt-2">Dessinez votre signature ci-dessous :</p>
          <SignatureCanvas
            onSave={handleSign}
            onCancel={() => setShowSign(null)}
          />
          {signing && <div className="text-center py-2"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#f7c935]" /></div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
